import { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Home, Lock, FileText, Gavel, Landmark, ReceiptText, ArrowRight, Phone } from "lucide-react";

import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

interface KnowRowProps {
  icon: FC<{ className?: string }>;
  label: string;
  value: string;
}

const KnowRow: FC<KnowRowProps> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 border-b border-primary bg-white p-4 last:border-b-0">
    <Icon className="h-4 w-4 shrink-0 text-secondary" />
    <div className="flex flex-1 items-center justify-between min-w-0">
      <span className="text-sm font-medium">{label}</span>
      <span className="text-sm text-secondary">{value}</span>
    </div>
  </div>
);

interface WhatWeKnowSidebarProps {
  familyCount: number;
  propertyCount: number;
  willStatus: string | null;
  keyDocCount: number;
  keyDocTotal: number;
  attorneyName: string | null;
  countyLabel: string | null;
  stateCode: string | null;
  decedentFirstName: string;
  expertName?: string | null;
}

export const WhatWeKnowSidebar: FC<WhatWeKnowSidebarProps> = ({
  familyCount,
  propertyCount,
  willStatus,
  keyDocCount,
  keyDocTotal,
  attorneyName,
  countyLabel,
  stateCode,
  decedentFirstName,
  expertName,
}) => {
  const navigate = useNavigate();

  const rows: { icon: FC<{ className?: string }>; label: string; value: string }[] = [];

  if (familyCount > 0) {
    rows.push({
      icon: Users,
      label: "Family map",
      value: `${familyCount} ${familyCount === 1 ? "person" : "people"}`,
    });
  }

  if (propertyCount > 0) {
    rows.push({
      icon: Home,
      label: "Property",
      value: `${propertyCount} confirmed`,
    });
  }

  if (willStatus) {
    rows.push({ icon: Lock, label: "Will", value: willStatus });
  }

  if (keyDocCount > 0) {
    rows.push({
      icon: FileText,
      label: "Documents",
      value: `${keyDocCount} of ${keyDocTotal} key docs`,
    });
  }

  if (attorneyName) {
    rows.push({ icon: Gavel, label: "Attorney", value: attorneyName });
  }

  if (expertName) {
    rows.push({ icon: Phone, label: "Your Estate Expert", value: expertName });
  }

  if (countyLabel && stateCode) {
    rows.push({
      icon: Landmark,
      label: "County",
      value: `${countyLabel}, ${stateCode}`,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {rows.length > 0 ? (
        <div className="flex flex-col gap-3">
          <p className="text-base font-semibold">What we know</p>
          <div className="rounded-lg border border-primary overflow-hidden">
            {rows.map((row) => (
              <KnowRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex gap-3 rounded-lg border border-primary bg-white p-4">
        <ReceiptText className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
        <div className="flex flex-1 flex-col gap-3">
          <p className="text-sm font-medium">
            Paying {decedentFirstName}&apos;s bills yourself? Save the receipts so the estate can
            pay you back once you have authority.
          </p>
          <button
            type="button"
            onClick={() => navigate(POST_LOSS_ROUTES.DOCUMENTS)}
            className="flex items-center gap-2 text-sm font-medium text-brand hover:underline"
          >
            Add a receipt
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <Phone className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
        <p className="text-sm text-secondary">
          Not sure where to start? Call us at (646) 692-0630
        </p>
      </div>
    </div>
  );
};
